import { inject, Injectable } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { IResponse } from '../../../../core/interfaces/IResponse';
import { ApiService } from '../../../../core/services/ApiService';
import { CardOperationType } from '../enums/CardOperationType';
import { ICard } from '../interfaces/ICard';
import { ICardType } from '../interfaces/ICardType';
import { CardsStore } from '../stores/CardsStore';
import { ICardProduct } from '../interfaces/ICardProduct';
import { CardActions } from '../enums/CardActions';
import { IPagedResults } from '../../../../core/interfaces/IPagedResults';
import { IPagination } from '../../../../core/interfaces/IPagination';
import { ICardTransaction } from '../interfaces/ICardTransaction';
import { ICardTicketHistoryRecord } from '../interfaces/ICardTicketHistoryRecord';
import { IIssuer } from '../interfaces/IIssuer';
import { environment } from '../../../../../environments/environment';
import dayjs from 'dayjs';
import { IAvailableProductForCard } from '../interfaces/IAvailableProductForCard';
import { IIdAndName } from '../../../../core/interfaces/IIdAndName';
import { HttpParams } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class CardsService {

  private readonly _basePath = '/cards';
  private readonly _apiService = inject(ApiService);
  private readonly _cardsStore = inject(CardsStore);

  public getCardTypes(): Observable<IResponse<ICardType[]>> {
    return this._apiService.get(`${this._basePath}/card-types?operationType=${CardOperationType.Sellable}`)
      .pipe(
        tap(res => this._cardsStore.cardTypes.set(res.data))
      );
  }

  public getUsableCardTypes(): Observable<IResponse<ICardType[]>> {
    return this._apiService.get(`${this._basePath}/card-types?operationType=${CardOperationType.Usable}`)
      .pipe(
        tap(res => this._cardsStore.usableCardTypes.set(res.data))
      );
  }

  public getCard(cardId: number): Observable<IResponse<ICard>> {
    return this._apiService.get(`${this._basePath}/${cardId}`);
  }

  public getCardProducts(cardId: number): Observable<IResponse<ICardProduct[]>> {
    return this._apiService.get(`${this._basePath}/${cardId}/products`);
  }

  public getCardByNumber(cardNumber: string): Observable<IResponse<ICard>> {
    return this._apiService.get(`${this._basePath}/by-number/${cardNumber}`);
  }

  public makeAction(
    cardId: number,
    cardNumber: string,
    action: CardActions,
    customerId: number = null
  ): Observable<IResponse<ICard>> {
    return this._apiService.post(
      `${this._basePath}/${cardId}/actions`,
      { actionType: action, customerId, cardNumber }
    );
  }

  public getCardTransactions(
    cardId: number,
    pagination: IPagination
  ): Observable<IResponse<IPagedResults<ICardTransaction>>> {
    return this._apiService.get(`${this._basePath}/${cardId}/transactions?page=${pagination.page}`);
  }

  public getAvailableProductsForCard(
    pagination: IPagination,
    productGroupIds: number[]
  ): Observable<IResponse<IPagedResults<IAvailableProductForCard>>> {
    let httpParams = new HttpParams().append('page', pagination.page);

    productGroupIds
      .forEach(id => httpParams = httpParams.append('filter', `productGroupId:${id}`));

    return this._apiService.get(`${this._basePath}/products/available`, httpParams);
  }

  public getCardTicketsHistory(
    cardId: number,
    pagination: IPagination
  ): Observable<IResponse<IPagedResults<ICardTicketHistoryRecord>>> {
    return this._apiService.get(`${this._basePath}/${cardId}/ticket-history?page=${pagination.page}`);
  }

  public deleteProductFromCard(cardId: number, cardProductId: number): Observable<IResponse<ICard>> {
    return this._apiService.delete(`${this._basePath}/${cardId}/products/${cardProductId}`);
  }

  public deleteFreeEntryFromCard(cardId: number, freeEntryId: number): Observable<IResponse<ICard>> {
    return this._apiService.delete(`${this._basePath}/${cardId}/free-entries/${freeEntryId}`);
  }

  public getIssuers(): Observable<IResponse<IIssuer[]>> {
    return this._apiService.get(`${this._basePath}/issuers`)
      .pipe(
        tap(res => this._cardsStore.issuers.set(res.data))
      );
  }

  public getProductGroups(): Observable<IResponse<IIdAndName<number>[]>> {
    return this._apiService.get(`${this._basePath}/product-groups`)
      .pipe(
        tap(res => this._cardsStore.productGroups.set(res.data))
      );
  }

  public addFreeEntries(
    cardId: number,
    data: { firmId: number; count: number; validFrom: string; validUntil: string; }
  ): Observable<IResponse<ICard>> {
    if (data.validFrom) {
      data.validFrom = dayjs(data.validFrom).format(environment.servicesDateFormat);
    }

    if (data.validUntil) {
      data.validUntil = dayjs(data.validUntil).format(environment.servicesDateFormat);
    }

    return this._apiService.post(`${this._basePath}/${cardId}/free-entries`, data);
  }

  public addProductsToCard(cardId: number, items: { productId: number; }[]): Observable<IResponse<ICard>> {
    return this._apiService.post(`${this._basePath}/${cardId}/products`, { items });
  }
}
